/**
 * Custom App Bar
 *
 * Top navigation bar with:
 * - Hamburger menu for tablet
 * - Title portal
 * - Centered app name (desktop only)
 * - Language selector
 * - User menu
 */

import React from 'react';
import { AppBar, TitlePortal, useSidebarState, LocalesMenuButton } from 'react-admin';
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { MyUserMenu } from './MyUserMenu';

const CustomToolbar = () => (
  <LocalesMenuButton />
);

export const CustomAppBar: React.FC = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useSidebarState();

  return (
    <AppBar
      userMenu={<MyUserMenu />}
      toolbar={<CustomToolbar />}
      elevation={0}
      sx={{
        backgroundColor: (theme.palette.background as any).appbar || theme.palette.background.default,
        color: theme.palette.text.primary,
        height: '44px',
        minHeight: '44px',
        position: isMobile ? 'fixed' : 'static',
        top: isMobile ? 0 : 'auto',
        left: isMobile ? 0 : 'auto',
        right: isMobile ? 0 : 'auto',
        zIndex: isMobile ? 1200 : 'auto',

        '& .MuiToolbar-root': {
          padding: { xs: '0 8px', sm: '0 16px' },
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '44px',
          minHeight: '44px !important',
        },
      }}
    >
      {/* Left section */}
      {!isMobile && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minWidth: 0,
          }}
        >
          <IconButton
            color="inherit"
            aria-label="menu"
            onClick={() => setOpen(!open)}
            edge="start"
            sx={{
              mr: 2,
              display: { md: 'none' },
              width: '40px',
              height: '40px',
            }}
          >
            <MenuIcon />
          </IconButton>

          <TitlePortal sx={{ fontWeight: 700, fontSize: '1.05rem' }} />
        </Box>
      )}

      {/* Center section - App name */}
      {!isMobile && (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pointerEvents: 'none',
            maxWidth: '80%',
            '& > *': { pointerEvents: 'auto' },
          }}
        >
          {isLargeScreen && (
            <Typography
              variant="h6"
              component="span"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 900,
                whiteSpace: 'nowrap',
                fontSize: '1.35rem',
                '@media (max-width: 1200px)': {
                  fontSize: '1.1rem',
                },
              }}
            >
              Roommatik Contacts
            </Typography>
          )}
        </Box>
      )}
    </AppBar>
  );
};

export default CustomAppBar;
